import 'maplibre-gl/dist/maplibre-gl.css';
import {
  AttributionControl,
  Map as MapLibreMap,
  NavigationControl,
  type CircleLayerSpecification,
  type GeoJSONSource,
  type GeoJSONSourceSpecification,
} from 'maplibre-gl';
import { useQuery } from '@tanstack/react-query';
import type { api } from '@udm/contracts';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { canAny, fetchAdoptionProposals, fetchDiscovered, fetchSiteAreas } from '../../api/client';
import { ArrowLeft, MapPinned, X } from 'lucide-react';
import { ProposalCard } from './ProposalCard';
import { ProposalsLayer } from './proposalsLayer';
import { useMe } from '../auth/session';
import { DiscoveredCard } from './DiscoveredCard';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useSearchParams } from 'react-router';
import { usePrefs } from '../../app/prefs';
import { dependantsIndex, deviceStatic, useStaticVersion } from '../../realtime/deviceStatic';
import { deviceLive } from '../../realtime/stores';
import { Card } from '../../ui/components';
import { baseStyle, cssVar } from './baseStyle';
import { DeviceCard } from './DeviceCard';
import { DevicesLayer, devicePaint, glowPaint, legendCategories, SOURCE_ID } from './devicesLayer';
import { highlightBounds, highlightOf, type MapHighlight } from './highlight';
import './highlight.css';
import { addOverlayLayers } from './overlays';
import { OverlayPanel } from './OverlayPanel';
import { ProjectFilter } from './ProjectFilter';

function areaBounds(fc: api.SiteAreas | undefined): [[number, number], [number, number]] | null {
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const f of fc?.features ?? [])
    for (const poly of f.geometry.coordinates)
      for (const ring of poly)
        for (const [x, y] of ring) {
          minX = Math.min(minX, x!);
          minY = Math.min(minY, y!);
          maxX = Math.max(maxX, x!);
          maxY = Math.max(maxY, y!);
        }
  return Number.isFinite(minX)
    ? [
        [minX, minY],
        [maxX, maxY],
      ]
    : null;
}

/** Area outlines (clients solid, localities/zones dashed), drawn under the devices. */
function addAreaLayers(map: MapLibreMap, fc: api.SiteAreas | undefined) {
  if (!fc || map.getSource('areas')) return;
  map.addSource('areas', { type: 'geojson', data: fc as unknown as GeoJSONSourceSpecification['data'] });
  map.addLayer({
    id: 'areas-fill',
    type: 'fill',
    source: 'areas',
    filter: ['==', ['get', 'kind'], 'organization'],
    paint: { 'fill-color': cssVar('--brand-cool') || '#1c63b7', 'fill-opacity': 0.06 },
  });
  map.addLayer({
    id: 'areas-line',
    type: 'line',
    source: 'areas',
    paint: {
      'line-color': cssVar('--brand-cool') || '#1c63b7',
      'line-width': ['case', ['==', ['get', 'kind'], 'organization'], 2, 1],
      'line-dasharray': [
        'case',
        ['==', ['get', 'kind'], 'organization'],
        ['literal', [1, 0]],
        ['literal', [3, 2]],
      ],
      'line-opacity': 0.8,
    },
  });
}

const DISCOVERED = 'discovered';

/**
 * Discovered (not adopted) devices that reported a position: a separate, small overlay in a colour that stands out,
 * drawn above the registry devices. Its data comes from the discovery list (refreshed every minute), never from the
 * realtime path of the registry layer.
 */
function addDiscoveredLayer(map: MapLibreMap) {
  if (map.getSource(DISCOVERED)) return;
  map.addSource(DISCOVERED, { type: 'geojson', data: { type: 'FeatureCollection', features: [] } });
  map.addLayer({
    id: 'discovered-circles',
    type: 'circle',
    source: DISCOVERED,
    paint: {
      'circle-radius': ['interpolate', ['linear'], ['zoom'], 8, 3.5, 13, 6, 16, 9],
      'circle-color': cssVar('--color-discovered'),
      'circle-stroke-width': 1.5,
      'circle-stroke-color': cssVar('--color-surface'),
    },
  });
}

function discoveredData(items: api.DiscoveredDevice[]): GeoJSONSourceSpecification['data'] {
  return {
    type: 'FeatureCollection',
    features: items
      .filter((d) => d.adoption?.position)
      .map((d) => ({
        type: 'Feature' as const,
        geometry: {
          type: 'Point' as const,
          coordinates: [d.adoption!.position!.lon, d.adoption!.position!.lat],
        },
        properties: { did: d.id },
      })),
  } as GeoJSONSourceSpecification['data'];
}

const HIGHLIGHT = 'highlight';
const HIGHLIGHT_FIT = { padding: 90, duration: 0, maxZoom: 17 };

/** Devices pointed out by another page (router state, e.g. a placement review group): rings above the markers. */
function addHighlightLayer(map: MapLibreMap) {
  if (map.getSource(HIGHLIGHT)) return;
  map.addSource(HIGHLIGHT, { type: 'geojson', data: { type: 'FeatureCollection', features: [] } });
  map.addLayer({
    id: 'highlight-rings',
    type: 'circle',
    source: HIGHLIGHT,
    paint: {
      'circle-radius': ['interpolate', ['linear'], ['zoom'], 8, 7, 13, 11, 16, 15],
      'circle-color': 'rgba(0,0,0,0)',
      'circle-stroke-width': 3,
      'circle-stroke-color': cssVar('--color-accent'),
    },
  });
}

function highlightData(h: MapHighlight | null): GeoJSONSourceSpecification['data'] {
  return {
    type: 'FeatureCollection',
    features: (h?.points ?? []).map((p) => ({
      type: 'Feature' as const,
      geometry: { type: 'Point' as const, coordinates: [p.lon, p.lat] },
      properties: { did: p.id },
    })),
  } as GeoJSONSourceSpecification['data'];
}

/** Shows only the devices of one project ('' = all): a layer filter, the source is not rebuilt. */
function applyProjectFilter(map: MapLibreMap, projectId: string) {
  for (const id of ['devices-glow', 'devices-circles'])
    if (map.getLayer(id)) map.setFilter(id, projectId ? ['==', ['get', 'pid'], projectId] : null);
}

function addDeviceLayer(map: MapLibreMap, data: GeoJSONSourceSpecification['data']) {
  map.addSource(SOURCE_ID, { type: 'geojson', data });
  // light glow under the markers: the marker shows connectivity/alarm, the glow only the light
  map.addLayer({
    id: 'devices-glow',
    type: 'circle',
    source: SOURCE_ID,
    paint: glowPaint(cssVar) as CircleLayerSpecification['paint'],
  });
  map.addLayer({
    id: 'devices-circles',
    type: 'circle',
    source: SOURCE_ID,
    paint: devicePaint(cssVar) as CircleLayerSpecification['paint'],
  });
}

/**
 * Map page. The device source is built once from the static snapshot; realtime changes go through
 * DevicesLayer.applyChanges → setFeatureState for changed devices only. Clustering for low zoom
 * levels is planned for the realtime phase (docs/architecture/06-frontend.md §4).
 */
export function MapPage() {
  const { t } = useTranslation();
  // The open device card lives in the URL (?device=<id>): shareable, survives reload, back button closes it.
  const [params, setParams] = useSearchParams();
  const selected = params.get('device');
  const location = useLocation();
  const navigate = useNavigate();
  const highlight = useMemo(() => highlightOf(location.state), [location.state]);
  const highlightRef = useRef(highlight);
  highlightRef.current = highlight;
  const stateRef = useRef<unknown>(location.state);
  stateRef.current = location.state;
  const select = useCallback(
    (id: string | null) =>
      setParams(
        (p) => {
          const n = new URLSearchParams(p);
          if (id) n.set('device', id);
          else n.delete('device');
          return n;
        },
        // keep the router state (a highlight) while device cards open and close
        { replace: Boolean(id && selectedRef.current), state: stateRef.current },
      ),
    [setParams],
  );
  const selectRef = useRef(select);
  selectRef.current = select;
  const selectedRef = useRef(selected);
  selectedRef.current = selected;
  // ?project=<id>: the map shows only that project's devices (U20, user request 2026-10-21)
  const project = params.get('project') ?? '';
  const projectRef = useRef(project);
  projectRef.current = project;
  const ref = useRef<HTMLDivElement>(null);
  const mapRef = useRef<{ map: MapLibreMap; layer: DevicesLayer; ready: boolean } | null>(null);
  const theme = usePrefs((s) => s.theme);
  const loaded = useStaticVersion((s) => s.loaded);
  const staticVersion = useStaticVersion((s) => s.version);
  // Areas visible to the user (server-scoped). Kept in a ref: they only affect the initial view and outlines.
  const me = useMe().data;
  const canProvision = canAny(me, 'device.provision');
  const discovered = useQuery({
    queryKey: ['admin', 'discovered'],
    queryFn: fetchDiscovered,
    enabled: canProvision,
    refetchInterval: 60_000,
  });
  const discoveredItems = useMemo(() => discovered.data?.items ?? [], [discovered.data]);
  const discoveredRef = useRef(discoveredItems);
  discoveredRef.current = discoveredItems;
  // ?discovered=<id>: "Show on map" from the discovery list opens that device's card (2026-10-10)
  const [pickedDiscovered, setPickedDiscovered] = useState<string | null>(() => params.get('discovered'));
  // On-demand overlay: communes with discovered devices outside every client area (looked up in the background).
  const [showProposals, setShowProposals] = useState(false);
  const [pickedProposal, setPickedProposal] = useState<string | null>(null);
  const proposals = useQuery({
    queryKey: ['admin', 'adoption-proposals'],
    queryFn: fetchAdoptionProposals,
    enabled: canProvision && showProposals,
    refetchInterval: (q) => (q.state.data?.pending ? 3000 : 60_000),
  });
  const proposalsRef = useRef<ProposalsLayer | null>(null);
  const lastProposal = useRef<api.AdoptionProposal | null>(null);
  const areas = useQuery({ queryKey: ['admin', 'site-areas'], queryFn: fetchSiteAreas, staleTime: 300_000 });
  const areasRef = useRef<api.SiteAreas | undefined>(undefined);
  areasRef.current = areas.data;
  // Map instance for the overlays panel (GIS assets, heat map), set once the style is loaded.
  const [overlayMap, setOverlayMap] = useState<MapLibreMap | null>(null);

  // Map lifetime: created once per mount (and on the rare explicit theme switch), never per event.
  useEffect(() => {
    if (!ref.current || !loaded) return;
    const map = new MapLibreMap({
      container: ref.current,
      style: baseStyle(theme),
      center: [27.1, 47.3],
      zoom: 9.5,
      attributionControl: false,
    });
    map.addControl(new NavigationControl({ showCompass: false }), 'bottom-right'); // top-right belongs to the device card
    map.addControl(new AttributionControl({ compact: false }), 'bottom-right'); // OSM attribution must stay visible
    const pts = [...deviceStatic.values()].filter((d) => d.lon !== undefined && d.lat !== undefined);
    // Drawn areas win: the map opens on the user's areas even without devices (see areas effect below).
    if (highlightRef.current) map.fitBounds(highlightBounds(highlightRef.current), HIGHLIGHT_FIT);
    else if (pts.length && !areasRef.current?.features.length) {
      const lons = pts.map((d) => d.lon!);
      const lats = pts.map((d) => d.lat!);
      map.fitBounds(
        [
          [Math.min(...lons), Math.min(...lats)],
          [Math.max(...lons), Math.max(...lats)],
        ],
        { padding: 60, duration: 0 },
      );
    }
    const layer = new DevicesLayer(map);
    const holder = { map, layer, ready: false };
    mapRef.current = holder;
    let unsubscribe: (() => void) | undefined;
    // 'style.load' fires as soon as the style is ready (not after all base tiles), so devices appear immediately.
    map.once('style.load', () => {
      addAreaLayers(map, areasRef.current);
      addDeviceLayer(
        map,
        layer.buildSource(
          [...deviceStatic.values()],
          new Set(dependantsIndex().keys()),
        ) as unknown as GeoJSONSourceSpecification['data'],
      );
      applyProjectFilter(map, projectRef.current);
      layer.reapplyAll((id) => deviceLive.get(id));
      layer.select(selectedRef.current, dependantsIndex().get(selectedRef.current ?? '') ?? []);
      addOverlayLayers(map, 'devices-glow');
      setOverlayMap(map);
      addDiscoveredLayer(map);
      addHighlightLayer(map);
      (map.getSource(HIGHLIGHT) as GeoJSONSource).setData(highlightData(highlightRef.current));
      proposalsRef.current = new ProposalsLayer(map, (ref) => {
        selectRef.current(null);
        setPickedDiscovered(null);
        setPickedProposal(ref);
      });
      (map.getSource(DISCOVERED) as GeoJSONSource).setData(discoveredData(discoveredRef.current));
      holder.ready = true;
      // Realtime path: only changed devices, only when their visual category changes.
      unsubscribe = deviceLive.subscribeChanges((changed) =>
        layer.applyChanges(changed, (id) => deviceLive.get(id)),
      );
    });
    // A click on a device opens its card; a click on empty map closes it.
    map.on('click', (e) => {
      const disc = map.getLayer('discovered-circles')
        ? map.queryRenderedFeatures(e.point, { layers: ['discovered-circles'] })[0]
        : undefined;
      const did = disc?.properties?.did as string | undefined;
      setPickedDiscovered(did ?? null);
      if (did) {
        setPickedProposal(null);
        return selectRef.current(null);
      }
      const hit = map.getLayer('devices-circles')
        ? map.queryRenderedFeatures(e.point, { layers: ['devices-circles'] })[0]
        : undefined;
      const id = typeof hit?.id === 'number' ? layer.deviceIdOf(hit.id) : undefined;
      selectRef.current(id ?? null);
      setPickedProposal(id ? null : (proposalsRef.current?.hit(e.point) ?? null));
    });
    map.on('mouseenter', 'devices-circles', () => (map.getCanvas().style.cursor = 'pointer'));
    map.on('mouseleave', 'devices-circles', () => (map.getCanvas().style.cursor = ''));
    map.on('mouseenter', 'discovered-circles', () => (map.getCanvas().style.cursor = 'pointer'));
    map.on('mouseleave', 'discovered-circles', () => (map.getCanvas().style.cursor = ''));
    return () => {
      unsubscribe?.();
      setOverlayMap(null);
      proposalsRef.current = null;
      mapRef.current = null;
      map.remove();
    };
  }, [theme, loaded]);

  useEffect(() => {
    const h = mapRef.current;
    if (h?.ready) h.layer.select(selected, dependantsIndex().get(selected ?? '') ?? []);
  }, [selected]);

  // When the areas arrive (or change), outline them and open the map on them.
  useEffect(() => {
    const h = mapRef.current;
    if (!h || !areas.data) return;
    const apply = () => {
      const src = h.map.getSource('areas') as GeoJSONSource | undefined;
      if (src) src.setData(areas.data as unknown as GeoJSONSourceSpecification['data']);
      else addAreaLayers(h.map, areas.data);
      const b = areaBounds(areas.data);
      // a highlight decides the view; the areas are only outlined then
      if (b && !highlightRef.current) h.map.fitBounds(b, { padding: 60, duration: 0, maxZoom: 16 });
    };
    if (h.map.isStyleLoaded()) apply();
    else h.map.once('style.load', apply);
  }, [areas.data, loaded]);

  // Highlight handed over by another page: rings + view on those devices; cleared when the state goes away.
  useEffect(() => {
    const h = mapRef.current;
    if (!h?.ready) return;
    (h.map.getSource(HIGHLIGHT) as GeoJSONSource | undefined)?.setData(highlightData(highlight));
    if (highlight) h.map.fitBounds(highlightBounds(highlight), HIGHLIGHT_FIT);
  }, [highlight]);

  // Proposals overlay: drawn only while switched on; removed completely otherwise.
  useEffect(() => {
    const h = mapRef.current;
    const layer = proposalsRef.current;
    if (!h?.ready || !layer) return;
    if (showProposals && proposals.data) layer.show(proposals.data.areas);
    else if (!showProposals) {
      layer.hide();
      setPickedProposal(null);
    }
  }, [showProposals, proposals.data]);

  // Discovery list refreshed (every minute): only the small overlay source is replaced.
  useEffect(() => {
    const h = mapRef.current;
    if (!h?.ready) return;
    (h.map.getSource(DISCOVERED) as GeoJSONSource | undefined)?.setData(discoveredData(discoveredItems));
  }, [discoveredItems]);

  useEffect(() => {
    const h = mapRef.current;
    if (h?.ready) applyProjectFilter(h.map, project);
  }, [project]);
  const showProjects = canAny(me, 'project.view') && canAny(me, 'operations.view');
  const chooseProject = (id: string) => {
    setParams(
      (p) => {
        const n = new URLSearchParams(p);
        if (id) n.set('project', id);
        else n.delete('project');
        return n;
      },
      { replace: true, state: stateRef.current },
    );
    // frame the project's devices
    const pts = id ? [...deviceStatic.values()].filter((d) => d.projectId === id && d.lon !== undefined) : [];
    if (pts.length && mapRef.current)
      mapRef.current.map.fitBounds(
        [
          [Math.min(...pts.map((d) => d.lon!)), Math.min(...pts.map((d) => d.lat!))],
          [Math.max(...pts.map((d) => d.lon!)), Math.max(...pts.map((d) => d.lat!))],
        ],
        { padding: 60, maxZoom: 16, duration: 600 },
      );
  };

  // Registry changes (rare: device added/moved) refresh the source data; realtime deltas never do.
  useEffect(() => {
    const h = mapRef.current;
    if (!h?.ready) return;
    const src = h.map.getSource(SOURCE_ID) as GeoJSONSource | undefined;
    src?.setData(
      h.layer.buildSource(
        [...deviceStatic.values()],
        new Set(dependantsIndex().keys()),
      ) as unknown as GeoJSONSourceSpecification['data'],
    );
    h.layer.reapplyAll((id) => deviceLive.get(id));
  }, [staticVersion]);

  return (
    <div className="map-wrap">
      <div ref={ref} style={{ position: 'absolute', inset: 0 }} />
      {selected && <DeviceCard key={selected} id={selected} onClose={() => select(null)} />}
      {highlight && (
        <div className="map-highlight" role="status">
          <span className="map-highlight-ring" aria-hidden="true" />
          <span className="ellipsis">
            {t('map.highlight.title', { count: highlight.points.length, label: highlight.label })}
          </span>
          {highlight.returnTo && (
            <button
              type="button"
              className="btn btn-ghost"
              onClick={() => void navigate(highlight.returnTo!.path, { state: highlight.returnTo!.state })}
            >
              <ArrowLeft size={15} /> {highlight.returnTo.label ?? t('map.highlight.back')}
            </button>
          )}
          <button
            type="button"
            className="icon-btn"
            title={t('map.highlight.clear')}
            aria-label={t('map.highlight.clear')}
            onClick={() =>
              void navigate({ pathname: location.pathname, search: location.search }, { replace: true })
            }
          >
            <X size={16} />
          </button>
        </div>
      )}
      {showProjects && !selected && !pickedProposal && !pickedDiscovered && (
        <ProjectFilter value={project} onChange={(id) => chooseProject(id)} />
      )}
      <div className="map-tools">
        <OverlayPanel map={overlayMap} />
        {canProvision && (
          <button
            type="button"
            className="btn map-tool-btn"
            aria-pressed={showProposals}
            onClick={() => setShowProposals((v) => !v)}
          >
            <MapPinned size={15} /> {t('map.proposals.toggle')}
            {showProposals && proposals.data && (
              <span className="muted small">
                {proposals.data.pending
                  ? t('map.proposals.pending', { count: proposals.data.pending })
                  : t('map.proposals.count', { count: proposals.data.areas.length })}
              </span>
            )}
          </button>
        )}
      </div>
      {!selected &&
        pickedProposal &&
        (() => {
          // a proposal leaves the list once a client covers it: the card keeps showing it (creation result)
          const p =
            proposals.data?.areas.find((a) => a.osm_ref === pickedProposal) ??
            (lastProposal.current?.osm_ref === pickedProposal ? lastProposal.current : undefined);
          lastProposal.current = p ?? null;
          return p ? <ProposalCard key={p.osm_ref} p={p} onClose={() => setPickedProposal(null)} /> : null;
        })()}
      {!selected &&
        !pickedProposal &&
        pickedDiscovered &&
        (() => {
          const d = discoveredItems.find((x) => x.id === pickedDiscovered);
          return d ? <DiscoveredCard key={d.id} d={d} onClose={() => setPickedDiscovered(null)} /> : null;
        })()}
      <div className="map-legend">
        <Card>
          {legendCategories(canAny(me, 'alarm.view')).map((c) => (
            <span key={c} className="toolbar">
              <span className="dot" style={{ color: `var(--color-${c})` }} />
              {c === 'warning' || c === 'critical' ? t(`severity.${c}`) : t(`presence.${c}`)}
            </span>
          ))}
          {canProvision && discoveredItems.length > 0 && (
            <span className="toolbar">
              <span className="dot" style={{ color: 'var(--color-discovered)' }} />
              {t('map.discovered.legend', {
                count: discoveredItems.filter((d) => d.adoption?.position).length,
                noPosition: discoveredItems.filter((d) => !d.adoption?.position).length,
              })}
            </span>
          )}
        </Card>
      </div>
    </div>
  );
}
